import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, BedDouble, Mic, Building2, Users, PartyPopper, Utensils, Sparkles } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

interface EventType {
  text: string;
  image: string;
  icon: React.ComponentType<{ className?: string }>;
}

const eventTypes: EventType[] = [
  {
    text: "Séjour avec nuitée",
    image:
      "https://cdn.builder.io/api/v1/image/assets/56829eea2c33408a99d45ea83a3459bb/44f55de0e2de97b946785edabf84877cd9f4a9b1?placeholderIfAbsent=true",
    icon: BedDouble,
  },
  { text: "Conférences", image: "/lovable-uploads/thales-conferences.webp", icon: Mic },
  {
    text: "Réservation de chambres pour expositions et salons",
    image: "/lovable-uploads/thales-rooms.webp",
    icon: Building2,
  },
  {
    text: "Journée externe | Réunion | Session de formation",
    image: "/lovable-uploads/thales-awayday.webp",
    icon: Users,
  },
  {
    text: "Soirées d'entreprise | Afterworks",
    image: "/lovable-uploads/thales-parties.webp",
    icon: PartyPopper,
  },
  { text: "Repas d'affaires | Traiteur", image: "/lovable-uploads/thales-meals.webp", icon: Utensils },
  { text: "Activité team building", image: "/lovable-uploads/thales-teambuilding.webp", icon: Sparkles },
];

const ThalesEventTypes = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  return (
    <section className="pt-4 md:pt-8 pb-6 md:pb-8 px-4 bg-white">
      <div className="max-w-[1280px] mx-auto">
        <div className="mb-8 md:mb-12 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#111111] max-w-3xl mx-auto leading-tight">
            Organisez tout type d'événement, de 5 à 500 participants, partout dans le monde
          </h2>
        </div>

        <div className="relative">
          <Carousel
            setApi={setApi}
            opts={{ align: "start", loop: true }}
            plugins={[Autoplay({ delay: 4500, stopOnInteraction: true })]}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {eventTypes.map((item, index) => {
                return (
                  <CarouselItem
                    key={index}
                    className="pl-4 basis-[70%] sm:basis-1/2 md:basis-1/3 lg:basis-1/4"
                  >
                    <div className="group relative rounded-2xl overflow-hidden aspect-[3/4] cursor-pointer">
                      <img
                        src={item.image}
                        alt={item.text}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                        <h3 className="text-white text-xl md:text-2xl lg:text-[26px] font-semibold leading-tight">
                          {item.text}
                        </h3>
                      </div>
                    </div>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
          </Carousel>

          {/* Side arrows */}
          <button
            type="button"
            onClick={() => api?.scrollPrev()}
            aria-label="Précédent"
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 w-12 h-12 rounded-full border border-black/10 bg-white hover:bg-[#C6E278] hover:border-[#C6E278] shadow-md transition-colors items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5 text-[#111111]" />
          </button>
          <button
            type="button"
            onClick={() => api?.scrollNext()}
            aria-label="Suivant"
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 w-12 h-12 rounded-full border border-black/10 bg-white hover:bg-[#C6E278] hover:border-[#C6E278] shadow-md transition-colors items-center justify-center"
          >
            <ChevronRight className="w-5 h-5 text-[#111111]" />
          </button>

          {/* Mobile arrows below */}
          <div className="flex md:hidden items-center justify-center gap-3 mt-6">
            <button
              type="button"
              onClick={() => api?.scrollPrev()}
              aria-label="Précédent"
              className="w-11 h-11 rounded-full border border-black/10 bg-white hover:bg-[#C6E278] hover:border-[#C6E278] transition-colors flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5 text-[#111111]" />
            </button>
            <button
              type="button"
              onClick={() => api?.scrollNext()}
              aria-label="Suivant"
              className="w-11 h-11 rounded-full border border-black/10 bg-white hover:bg-[#C6E278] hover:border-[#C6E278] transition-colors flex items-center justify-center"
            >
              <ChevronRight className="w-5 h-5 text-[#111111]" />
            </button>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {eventTypes.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => api?.scrollTo(i)}
              aria-label={`Aller à l'événement ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === current ? "w-8 bg-[#111111]" : "w-1.5 bg-black/20 hover:bg-black/40"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ThalesEventTypes;
